import { useEffect, useMemo, useRef } from "react";
import { useTabs } from "./hooks";
import uniqBy from "lodash/uniqBy";

import type { ElementType, ReactNode, RefObject } from "react";
import type { Tab } from "./providers";

import styles from "./tabs-item.module.css";

export type TabsItemProps<T extends ElementType = "div"> = {
  children: ReactNode;
  name: string;
  as?: T;
  width?: number;
} & React.ComponentPropsWithoutRef<T>;

export function TabsItem<T extends ElementType = "div">({
  name,
  children,
  as,
  width,
  ...props
}: TabsItemProps<T>) {
  const { handleChange, isActive, setTabs } = useTabs();
  const ref = useRef<HTMLElement>(null);

  const Component = as || "div";

  const className = useMemo(() => {
    if (isActive(name)) {
      return `${styles.Container} ${styles.Active}`;
    }
    return styles.Container;
  }, [isActive, name]);

  useEffect(() => {
    setTabs((tabs: Tab[]) => {
      return uniqBy(
        [
          ...tabs,
          {
            name,
            ref: ref as RefObject<HTMLDivElement>,
          },
        ],
        (tab) => tab.name
      );
    });
  }, [name, setTabs]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleChange(name);
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (as === "a") {
      e.preventDefault();
    }
    handleChange(name);
  };

  return (
    <Component
      role="tab"
      aria-selected={isActive(name)}
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={className}
      style={{ width }}
      ref={ref as RefObject<HTMLDivElement>}
      {...props}
    >
      {children}
    </Component>
  );
}
